import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { TemplateContent } from "@/types/template";
import { useTemplateStore } from "@/store/useTemplateStore";
import { isSeedId } from "@/store/seedTemplates";
import { parseTemplate } from "@/parser/templateParser";
import { PromptDialog } from "@/components/ui/PromptDialog";
import { useToast } from "@/hooks/useToast";
import { groupByCategory } from "@/utils/templateGroups";

const ICON_STROKE = { stroke: "currentColor", strokeLinecap: "round" as const, strokeWidth: 2 };

function EmptyTemplateIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect
        x="8"
        y="4"
        width="32"
        height="40"
        rx="3"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      />
      <line x1="14" y1="14" x2="34" y2="14" {...ICON_STROKE} />
      <line x1="14" y1="22" x2="34" y2="22" {...ICON_STROKE} />
      <line x1="14" y1="30" x2="34" y2="30" {...ICON_STROKE} />
      <line x1="14" y1="38" x2="24" y2="38" {...ICON_STROKE} />
    </svg>
  );
}

function TemplateEmptyState({ onNew }: Readonly<{ onNew: () => void }>) {
  return (
    <div className="empty-state">
      <div className="empty-state-icon">
        <EmptyTemplateIcon />
      </div>
      <div className="empty-state-title">No templates yet</div>
      <div className="empty-state-description">
        Create your first template to start building clinical documentation.
      </div>
      <button className="primary" onClick={onNew}>
        + New Template
      </button>
    </div>
  );
}

type TemplateFilter = "all" | "built-in" | "user";

interface ParsedTemplate {
  id: string;
  parsed: TemplateContent;
  category?: string;
}

function TemplateBadge({ id }: Readonly<{ id: string }>) {
  const seed = isSeedId(id);
  return (
    <span className={seed ? "seed-badge" : "seed-badge user-badge"}>
      {seed ? "Built-in" : "User"}
    </span>
  );
}

function TemplateFilterBar({
  filter,
  setFilter,
}: Readonly<{ filter: TemplateFilter; setFilter: (f: TemplateFilter) => void }>) {
  const options: { label: string; value: TemplateFilter }[] = [
    { label: "All", value: "all" },
    { label: "Built-in", value: "built-in" },
    { label: "User", value: "user" },
  ];
  return (
    <div className="template-filter-bar">
      {options.map((o) => (
        <button
          key={o.value}
          className={`template-filter-btn${filter === o.value ? " active" : ""}`}
          onClick={() => {
            setFilter(o.value);
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function TemplateGroups({
  onNavigate,
  parsed,
}: Readonly<{ parsed: ParsedTemplate[]; onNavigate: (id: string) => void }>) {
  const groups = groupByCategory(parsed);
  return (
    <div className="template-groups">
      {groups.map((group) => (
        <section key={group.label} className="template-group">
          <h3 className="template-group-label">{group.label}</h3>
          <ul className="template-cards">
            {group.items.map((t) => (
              <li
                key={t.id}
                className="template-card"
                onClick={() => {
                  onNavigate(t.id);
                }}
              >
                <span className="template-card-name">{t.parsed.name}</span>
                <span className="template-card-right">
                  <TemplateBadge id={t.id} />
                  <span className="field-count">
                    {t.parsed.content.filter((n) => n.type === "field").length} fields
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function applyFilter(templates: ParsedTemplate[], filter: TemplateFilter): ParsedTemplate[] {
  if (filter === "built-in") {
    return templates.filter((t) => isSeedId(t.id));
  }
  if (filter === "user") {
    return templates.filter((t) => !isSeedId(t.id));
  }
  return templates;
}

function useTemplateListState() {
  const templates = useTemplateStore((s) => s.templates);
  const createTemplate = useTemplateStore((s) => s.create);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showPrompt, setShowPrompt] = useState(false);
  const [filter, setFilter] = useState<TemplateFilter>("all");

  const parsed = useMemo(
    () =>
      templates.map((t) => {
        const content = parseTemplate(t.source).content;
        return { ...t, category: content.category, parsed: content };
      }),
    [templates],
  );

  const filtered = useMemo(() => applyFilter(parsed, filter), [parsed, filter]);

  const handleCreate = async (name: string) => {
    const t = await createTemplate(name, false);
    setShowPrompt(false);
    toast("Template created", "success");
    void navigate(`/build/${t.id}`);
  };

  return { filter, filtered, handleCreate, navigate, parsed, setFilter, setShowPrompt, showPrompt };
}

export function TemplateList() {
  const { filter, filtered, handleCreate, navigate, parsed, setFilter, setShowPrompt, showPrompt } =
    useTemplateListState();

  return (
    <div className="template-list-page">
      <div className="template-list-header">
        <h2>Templates</h2>
        <button
          className="primary"
          onClick={() => {
            setShowPrompt(true);
          }}
        >
          + New Template
        </button>
      </div>

      {parsed.length > 0 && <TemplateFilterBar filter={filter} setFilter={setFilter} />}

      <PromptDialog
        open={showPrompt}
        title="New Template"
        label="Template name"
        placeholder="e.g., Balance Assessment"
        onConfirm={handleCreate}
        onCancel={() => {
          setShowPrompt(false);
        }}
      />

      {parsed.length === 0 ? (
        <TemplateEmptyState
          onNew={() => {
            setShowPrompt(true);
          }}
        />
      ) : (
        <TemplateGroups parsed={filtered} onNavigate={(id) => navigate(`/build/${id}`)} />
      )}
    </div>
  );
}
